import {
  getHeadStyleOptions,
  getPostFinish,
  getSurfaceFinish,
  getThreadPitchOptions,
  getThreadSizeOptions,
} from '@calculations/feature/alpha/look-ups';
import { UnreachableCaseError } from '@errors/unreachable';
import { AlphaStaticData } from '@snap-types/features/alpha/models/alpha-static-data';
import {
  AutoConfiguratorState,
  LineItemFieldKey,
  StaticDataSource,
  ValueId,
} from '@snap-types/models/auto-configurator/state';

import { getLineItem } from './line-item';
import { getSelectChoiceValue } from './value/get-value';

function getStaticData(state: AutoConfiguratorState): AlphaStaticData {
  const staticData = state.staticData;

  if (!staticData) {
    throw new Error('No static data found.');
  }

  return staticData;
}

type GetNarrowedStaticDataInputs = {
  state: AutoConfiguratorState;
  name: StaticDataSource;
  quoteLineUuid: string;
};

export function getNarrowedStaticData(
  inputs: GetNarrowedStaticDataInputs,
): string[] {
  const staticData = getStaticData(inputs.state);
  const quoteLine = getLineItem(inputs.state, inputs.quoteLineUuid);

  function buildValueId(key: LineItemFieldKey): ValueId {
    return { key, model: 'lineItem', uuid: quoteLine.uuid };
  }

  switch (inputs.name) {
    case StaticDataSource.headStyle: {
      const material = getSelectChoiceValue(
        inputs.state,
        buildValueId('material'),
      );

      if (!material) {
        return [];
      }

      return getHeadStyleOptions({
        material,
        headStyleOptions: staticData.headStyle,
      }).match(
        (ok) => ok,
        () => [],
      );
    }

    case StaticDataSource.surfaceFinish: {
      const material = getSelectChoiceValue(
        inputs.state,
        buildValueId('material'),
      );

      if (!material) {
        return [];
      }

      return getSurfaceFinish({
        material,
        surfaceFinishOptions: staticData.surfaceFinish,
      }).match(
        (ok) => ok,
        () => [],
      );
    }

    case StaticDataSource.threadSize: {
      const material = getSelectChoiceValue(
        inputs.state,
        buildValueId('material'),
      );
      const fastenerType = getSelectChoiceValue(
        inputs.state,
        buildValueId('fastenerType'),
      );

      if (!material || !fastenerType) {
        return [];
      }

      return getThreadSizeOptions({
        material,
        fastenerType,
        threadSizeOptions: staticData.threadSize,
      }).match(
        (ok) => ok,
        () => [],
      );
    }

    case StaticDataSource.threadPitch: {
      const fastenerType = getSelectChoiceValue(
        inputs.state,
        buildValueId('fastenerType'),
      );
      const threadSize = getSelectChoiceValue(
        inputs.state,
        buildValueId('threadSize'),
      );

      if (!fastenerType || !threadSize) {
        return [];
      }

      return getThreadPitchOptions({
        fastenerType,
        threadSize,
        threadPitchOptions: staticData.threadPitch,
      }).match(
        (ok) => ok,
        () => [],
      );
    }

    case StaticDataSource.postFinish: {
      const surfaceFinish = getSelectChoiceValue(
        inputs.state,
        buildValueId('surfaceFinish'),
      );

      if (!surfaceFinish) {
        return [];
      }

      return getPostFinish({
        surfaceFinish,
        postFinishOptions: staticData.postFinish,
      }).match(
        (ok) => ok,
        () => [],
      );
    }

    case StaticDataSource.fastenerType:
      return staticData.fastenerType;

    case StaticDataSource.materialGrade:
      return staticData.materialGrade;

    case StaticDataSource.driveRecessStyle:
      return staticData.driveRecessStyle;

    case StaticDataSource.osps:
      return staticData.osps;

    case StaticDataSource.sems:
      return staticData.sems;

    case StaticDataSource.unitOfMeasure:
      return staticData.unitOfMeasure;

    default:
      throw new UnreachableCaseError(inputs.name);
  }
}
